import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSkeleton } from '../DashboardLoader';
import {
  Users,
  Store,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  BarChart3,
  PieChart,
  CalendarDays,
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useAdminDashboard } from '../../../hooks/useAdminDashboard';
import { formatCurrency, formatNumber } from '../../../utils/formatPrice';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler);

const statCardClass = 'bg-white p-6 rounded-2xl border border-slate-200';
const statIconWrap = 'p-3 rounded-xl bg-slate-50 border border-slate-200 flex-shrink-0';
const statIconClass = 'w-6 h-6 text-slate-600';
const statTitleClass = 'text-sm font-semibold text-slate-700';
const statValueClass = 'text-[1.7rem] font-bold text-slate-900';

const PRIMARY = 'rgb(5, 70, 37)';
const PRIMARY_SOFT = 'rgba(5, 70, 37, 0.12)';
const MUTED_LINE = 'rgb(107, 114, 128)';

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function orderStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  const map = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: 'Prête',
    delivered: 'Livrée',
    completed: 'Terminée',
    cancelled: 'Annulée',
    paid: 'Payée',
  };
  return map[s] || status || '—';
}

const chartOptionsBase = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 12, font: { size: 11 } },
    },
  },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const {
    stats,
    recentActivities,
    topRestaurants,
    dailyReport,
    loading,
    error,
    refetch
  } = useAdminDashboard();

  const indices = dailyReport?.indices;
  const daily = dailyReport?.daily || [];
  const statusDist = dailyReport?.order_status_distribution || [];

  const lineChartData = useMemo(() => {
    if (!daily.length) return null;
    return {
      labels: daily.map((d) => formatShortDate(d.date)),
      datasets: [
        {
          label: 'Commandes',
          data: daily.map((d) => d.orders),
          borderColor: PRIMARY,
          backgroundColor: PRIMARY_SOFT,
          fill: true,
          tension: 0.35,
          yAxisID: 'y',
          pointRadius: 2,
        },
        {
          label: 'CA (€)',
          data: daily.map((d) => d.revenue),
          borderColor: MUTED_LINE,
          backgroundColor: 'rgba(107, 114, 128, 0.08)',
          fill: true,
          tension: 0.35,
          yAxisID: 'y1',
          pointRadius: 2,
        },
      ],
    };
  }, [daily]);

  const lineChartOptions = useMemo(
    () => ({
      ...chartOptionsBase,
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } } },
        y: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Commandes', font: { size: 11 } },
          grid: { color: 'rgba(0,0,0,0.04)' },
          ticks: { stepSize: 1 },
        },
        y1: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'CA (€)', font: { size: 11 } },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    []
  );

  const ordersCompareData = useMemo(() => {
    if (!indices) return null;
    return {
      labels: ['7 derniers jours', '7 jours précédents'],
      datasets: [
        {
          label: 'Commandes',
          data: [indices.last7_orders ?? 0, indices.prev7_orders ?? 0],
          backgroundColor: [PRIMARY, 'rgba(156, 163, 175, 0.85)'],
          borderRadius: 8,
        },
      ],
    };
  }, [indices]);

  const revenueCompareData = useMemo(() => {
    if (!indices) return null;
    return {
      labels: ['7 derniers jours', '7 jours précédents'],
      datasets: [
        {
          label: 'CA (€)',
          data: [indices.last7_revenue ?? 0, indices.prev7_revenue ?? 0],
          backgroundColor: ['rgba(5, 70, 37, 0.55)', 'rgba(156, 163, 175, 0.55)'],
          borderRadius: 8,
        },
      ],
    };
  }, [indices]);

  const simpleBarOptions = useMemo(
    () => ({
      ...chartOptionsBase,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(ctx) {
              const v = ctx.raw;
              if (ctx.dataset.label === 'CA (€)') return `${Number(v).toFixed(2)} €`;
              return `${v} commandes`;
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)' } },
      },
    }),
    []
  );

  const doughnutData = useMemo(() => {
    if (!statusDist.length) return null;
    const labels = statusDist.map((x) => orderStatusLabel(x.status));
    const data = statusDist.map((x) => x.count);
    const bg = [
      'rgba(5, 70, 37, 0.85)',
      'rgba(5, 70, 37, 0.55)',
      'rgba(107, 114, 128, 0.75)',
      'rgba(156, 163, 175, 0.6)',
      'rgba(209, 213, 219, 0.9)',
      'rgba(5, 70, 37, 0.35)',
    ];
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: data.map((_, i) => bg[i % bg.length]),
          borderWidth: 0,
        },
      ],
    };
  }, [statusDist]);

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } },
        tooltip: {
          callbacks: {
            label(ctx) {
              const total = dailyReport?.order_status_total || 1;
              const v = ctx.raw || 0;
              const pct = ((v / total) * 100).toFixed(1);
              return `${ctx.label}: ${v} (${pct}%)`;
            },
          },
        },
      },
    }),
    [dailyReport?.order_status_total]
  );

  const pctStr = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v}%`);

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <h2 className="text-lg font-semibold text-red-800">Erreur de chargement</h2>
          </div>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <PageSkeleton />;

  return (
    <div className="min-h-screen bg-white p-6 text-slate-900">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Tableau de bord Administrateur</h1>
        <p className="text-xs font-light text-slate-500 mt-1">Vue d'ensemble de la plateforme namke</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Utilisateurs</p>
              <p className={statValueClass}>{formatNumber(stats.totalUsers)}</p>
            </div>
            <div className={statIconWrap}>
              <Users className={statIconClass} />
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Restaurants</p>
              <p className={statValueClass}>{stats.totalRestaurants}</p>
            </div>
            <div className={statIconWrap}>
              <Store className={statIconClass} />
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Commandes</p>
              <p className={statValueClass}>{formatNumber(stats.totalOrders)}</p>
            </div>
            <div className={statIconWrap}>
              <ShoppingBag className={statIconClass} />
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Revenus</p>
              <p className={statValueClass}>{formatCurrency(stats.totalRevenue)}</p>
            </div>
            <div className={statIconWrap}>
              <DollarSign className={statIconClass} />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Restaurants en attente</p>
              <p className={statValueClass}>{stats.pendingRestaurants}</p>
            </div>
            <div className={statIconWrap}>
              <AlertCircle className={statIconClass} />
            </div>
          </div>
        </div>

        <div className={statCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={statTitleClass}>Utilisateurs actifs</p>
              <p className={statValueClass}>{stats.activeUsers}</p>
            </div>
            <div className={statIconWrap}>
              <TrendingUp className={statIconClass} />
            </div>
          </div>
        </div>
      </div>

      {/* Indices & rapport journalier */}
      {indices && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Indices & rapport journalier</h2>
            <span className="text-xs text-gray-500">({dailyReport?.period_days || 14} jours)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-2xl border border-slate-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aujourd&apos;hui</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{indices.today_orders} cmd</p>
              <p className="text-sm text-gray-600">{formatCurrency(indices.today_revenue)}</p>
              <p className="text-xs text-gray-500 mt-2">
                vs veille : commandes {pctStr(indices.orders_vs_yesterday_pct)} · CA {pctStr(indices.revenue_vs_yesterday_pct)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hier</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{indices.yesterday_orders} cmd</p>
              <p className="text-sm text-gray-600">{formatCurrency(indices.yesterday_revenue)}</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">7 derniers jours</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{indices.last7_orders} cmd</p>
              <p className="text-sm text-gray-600">{formatCurrency(indices.last7_revenue)}</p>
              <p className="text-xs text-gray-500 mt-2">vs semaine précédente : {pctStr(indices.orders_week_over_week_pct)} cmd</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-slate-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">CA 7j vs semaine préc.</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{pctStr(indices.revenue_week_over_week_pct)}</p>
              <p className="text-xs text-gray-500 mt-2">
                {formatCurrency(indices.prev7_revenue)} → {formatCurrency(indices.last7_revenue)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-800">Tendance commandes & CA</h3>
              </div>
              {lineChartData ? (
                <div className="h-64">
                  <Line data={lineChartData} options={lineChartOptions} />
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-8 text-center">Pas assez de données sur la période.</p>
              )}
            </div>

            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <PieChart className="w-4 h-4 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-800">Statuts des commandes</h3>
              </div>
              {doughnutData ? (
                <div className="h-64">
                  <Doughnut data={doughnutData} options={doughnutOptions} />
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-8 text-center">Aucune commande.</p>
              )}
            </div>
          </div>

          {ordersCompareData && revenueCompareData && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Commandes — 7j vs 7j préc.</h3>
                <p className="text-xs text-gray-500 mb-3">Volumes sur deux fenêtres glissantes.</p>
                <div className="h-52">
                  <Bar data={ordersCompareData} options={simpleBarOptions} />
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Chiffre d&apos;affaires — 7j vs 7j préc.</h3>
                <p className="text-xs text-gray-500 mb-3">Somme des lignes de commande (€).</p>
                <div className="h-52">
                  <Bar data={revenueCompareData} options={simpleBarOptions} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!dailyReport && (
        <div className="mb-8 p-4 bg-amber-50 text-amber-900 rounded-lg text-sm">
          Le rapport journalier (graphiques) n&apos;a pas pu être chargé. Les totaux ci-dessus restent disponibles.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activities */}
        <div className="bg-white rounded-lg">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Activités récentes</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div
                    className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                      activity.status === 'success'
                        ? 'bg-green-500'
                        : activity.status === 'warning'
                          ? 'bg-yellow-500'
                          : 'bg-orange-500'
                    }`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                  <div className="flex-shrink-0">
                    {activity.status === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {activity.status === 'warning' && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                    {activity.status === 'pending' && <XCircle className="w-4 h-4 text-orange-500" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Restaurants */}
        <div className="bg-white rounded-lg">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Top Restaurants</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topRestaurants.map((restaurant, index) => (
                <div key={restaurant.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{restaurant.name}</p>
                      <p className="text-xs text-gray-500">
                        {restaurant.orders} commandes • {formatCurrency(restaurant.revenue || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{restaurant.rating}</span>
                      <span className="text-xs text-gray-500 ml-1">★</span>
                    </div>
                    <button
                      type="button"
                      title="Voir la fiche publique"
                      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
